// Nomad-mon Go! — title12 MENU ROW (was the v152 carousel; file name kept so
// the import stays put). Three plain-text items, all visible: Open Pack ·
// Battle · Friends, all the same size. main.js marks the lead
// (rail.dataset.lead); this module paints it .is-lead (brighter only),
// mirrors the lead's .sub into #titleInfo, and gives every tap the detent
// "tock" + a haptic buzz. The click itself still reaches the shell.js
// handler untouched. While packs wait (#menuPack.packs-ready, main.js
// paintEconomy) Open Pack gets its attention treatment: a light wisp orbiting
// the label (one small canvas), a rainbow sweep through the letters and a
// slow breath (CSS transforms only). Presentation only: never touches game state.
import * as audio from './audio.js';
import * as haptics from './haptics.js';

export function initTitleCarousel() {
  const rail = document.getElementById('titleCarousel');
  const info = document.getElementById('titleInfo');
  if (!rail) return;
  audio.loadSfx(['tock']).catch(() => {});
  const items = [...rail.querySelectorAll('button')];
  let lead = null;

  const paint = () => {
    const next = (rail.dataset.lead && document.getElementById(rail.dataset.lead)) || items[0];
    if (next !== lead) {
      for (const el of items) el.classList.toggle('is-lead', el === next);
      lead = next;
    }
    if (!info || !lead) return;
    const sub = lead.querySelector('.sub');
    const txt = sub ? sub.textContent.trim() : '';
    if (info.textContent !== txt) {
      info.textContent = txt;
      info.classList.remove('in'); void info.offsetWidth; info.classList.add('in');
    }
  };

  rail.addEventListener('click', (e) => {
    const b = e.target instanceof Element ? e.target.closest('button') : null;
    if (!b || !items.includes(b)) return;
    haptics.buzz(15);
    audio.play('tock', { gain: 0.42, rate: 1 + (items.indexOf(b) - 1) * 0.04 });
  }, true);

  // titlelock: the spring press (CSS .tc-press). A class rather than :active, because iOS only
  // paints :active with a touch listener and it drops it as soon as the finger moves a little.
  let pressed = null;
  const release = () => { if (pressed) { pressed.classList.remove('tc-press'); pressed = null; } };
  rail.addEventListener('pointerdown', (e) => {
    const b = e.target instanceof Element ? e.target.closest('button') : null;
    if (!b || !items.includes(b) || (e.button && e.pointerType === 'mouse')) return;
    release();
    pressed = b; b.classList.add('tc-press');
  });
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave']) rail.addEventListener(ev, release);
  addEventListener('blur', release);

  // Enter with nothing focused activates the lead
  addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || !document.body.classList.contains('on-title')) return;
    const ae = document.activeElement;
    if (ae && ae !== document.body) return;
    if (document.querySelector('#settingsDrawer.open, #pickerOverlay.open')) return;
    if (lead) { e.preventDefault(); lead.click(); }
  });

  initPackWisp(document.getElementById('menuPack'), (b) => b.classList.contains('packs-ready') && !b.classList.contains('cta-yield'));
  // QA-A: after the unlock (main.js .cta-next) the same wisp points at Campaign
  initPackWisp(document.getElementById('menuSolo'), (b) => b.classList.contains('cta-next'));

  new MutationObserver(paint).observe(rail, { attributes: true, attributeFilter: ['data-lead'],
    subtree: true, childList: true, characterData: true });
  paint();
}

// ---- Open Pack attention: rainbow sweep layer + orbiting wisp ----
// The sweep is a soft window (.tc-rb) that slides across the label while the
// rainbow copy inside it slides the other way, so the colours stay pinned to
// the letters: transform-only, no animated filters or background-position.
// The wisp is one canvas a little larger than the label; its rAF runs only
// while the title shows and packs wait. Reduced motion: one static glow.
function initPackWisp(btn, wants) {
  const label = btn && btn.querySelector('.tl');
  if (!label) return;
  const rb = document.createElement('span');
  rb.className = 'tc-rb';
  rb.setAttribute('aria-hidden', 'true');
  const ink = document.createElement('span');
  ink.textContent = label.textContent;
  rb.appendChild(ink);
  label.appendChild(rb);
  const cv = document.createElement('canvas');
  cv.className = 'tc-wisp';
  cv.setAttribute('aria-hidden', 'true');
  label.appendChild(cv);
  const g = cv.getContext('2d');
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const PAD = 26;                      // css px of canvas beyond the label on every side
  let raf = 0, w = 0, h = 0, dpr = 1;

  const live = () => wants(btn) && document.body.classList.contains('on-title') &&
    !document.hidden && label.offsetWidth > 0;
  const fit = () => {
    const lw = label.offsetWidth, lh = label.offsetHeight;
    dpr = Math.min(2, devicePixelRatio || 1);
    w = lw + PAD * 2; h = lh + PAD * 2;
    const W = Math.round(w * dpr), H = Math.round(h * dpr);
    if (cv.width !== W || cv.height !== H) { cv.width = W; cv.height = H; }
  };
  const draw = (t) => {
    const gold = btn.classList.contains('goldnext');
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    g.globalCompositeOperation = 'lighter';
    const cx = w / 2, cy = h / 2;
    const rx = w / 2 - PAD + 14, ry = h / 2 - PAD + 10;
    const tilt = -0.16, ct = Math.cos(tilt), st = Math.sin(tilt);
    const at = (a) => {                // ellipse point, tilted; z: +1 front (below), -1 back
      const x = Math.cos(a) * rx, y = Math.sin(a) * ry;
      return [cx + x * ct - y * st, cy + x * st + y * ct, Math.sin(a)];
    };
    const hue = (k) => gold ? 44 - 16 * k : (190 + 150 * k + t * 0.03) % 360;
    if (still.matches) {               // reduced motion: a static glow, no orbit
      const [x, y] = at(-0.55);
      dot(x, y, 9, hue(0), 0.75);
      return;
    }
    const a0 = t * 0.0021;             // ~3s per orbit
    const N = 44, span = 2.6;          // trail samples + arc length (radians)
    g.lineCap = 'butt';
    let [px, py] = at(a0 - span);
    for (let i = N - 1; i >= 0; i--) {  // tail -> head: a ribbon that thickens and brightens
      const k = i / N;
      const [x, y, z] = at(a0 - span * k);
      const depth = 0.6 + 0.4 * (z + 1) / 2;
      const f = (1 - k) * (1 - k);
      g.strokeStyle = `hsla(${hue(k)},100%,72%,${0.22 * f * depth})`;
      g.lineWidth = (2 + 7 * f) * depth;
      g.beginPath(); g.moveTo(px, py); g.lineTo(x, y); g.stroke();
      g.strokeStyle = `hsla(${hue(k)},100%,88%,${0.8 * f * depth})`;
      g.lineWidth = (0.6 + 1.6 * f) * depth;
      g.beginPath(); g.moveTo(px, py); g.lineTo(x, y); g.stroke();
      px = x; py = y;
    }
    const [hx, hy, hz] = at(a0);
    const hd = 0.6 + 0.4 * (hz + 1) / 2;
    dot(hx, hy, 13 * hd, hue(0), 0.9 * hd);
    g.fillStyle = `rgba(255,255,255,${hd})`;
    g.beginPath(); g.arc(hx, hy, 2 * hd, 0, Math.PI * 2); g.fill();
  };
  function dot(x, y, r, hu, a) {
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, `hsla(${hu},100%,86%,${a})`);
    gr.addColorStop(0.35, `hsla(${hu},100%,70%,${a * 0.45})`);
    gr.addColorStop(1, `hsla(${hu},100%,60%,0)`);
    g.fillStyle = gr;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  const frame = (t) => {
    raf = 0;
    if (!live()) { g.clearRect(0, 0, cv.width, cv.height); return; }
    fit(); draw(t);
    if (!still.matches) raf = requestAnimationFrame(frame);
  };
  const kick = () => { if (!raf) raf = requestAnimationFrame(frame); };
  new MutationObserver(kick).observe(btn, { attributes: true, attributeFilter: ['class'] });
  new MutationObserver(kick).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  document.addEventListener('visibilitychange', kick);
  addEventListener('resize', kick);
  if (still.addEventListener) still.addEventListener('change', kick);
  kick();
}
